import { useState } from 'react';

import { useSubscribeOn } from 'app/utils/useSubscribeOn';
import { MapBuilder } from 'app/utils/MapBuilder';

/**
 * Класс диапазон.
 */
class Range {
    private readonly _start: number;

    private readonly _end: number;

    constructor(start: number, end: number) {
        this._start = start;
        this._end = end;
    }

    /**
     * Метод для создания экземпляра класса {@link Range}.
     * @param start - нижняя граница диапазона.
     * @param end - - верхняя граница диапазона.
     */
    static of(start: number, end: number) {
        return new Range(start, end);
    }

    /**
     * Нижняя граница диапазона.
     */
    get start() {
        return this._start;
    }

    /**
     * Верхняя граница диапазона.
     */
    get end() {
        return this._end;
    }

    /**
     * Проверяет попадает ли значение в диапазон.
     *
     * @param value - значение.
     */
    isInBound(value: number) {
        return this.start <= value && this.end >= value;
    }
}

/**
 * Названия размеров окна.
 */
export enum WindowTypeName {
    DESKTOP = 'DESKTOP',
    MOBILE = 'MOBILE',
    TABLET = 'TABLET',
}

/**
 * Класс содержащий данные о возможных значениях ширины окна.
 */
class WindowWidthSizeType {
    /**
     * Имя типа окна.
     */
    private readonly name: WindowTypeName;

    /**
     * Диапазон ширины окна.
     */
    private readonly range: Range;

    private constructor(name: WindowTypeName, range: Range) {
        this.name = name;
        this.range = range;
    }

    /**
     * Характеристики всех размеров окна.
     */
    static readonly windowWidthSizeTypes: WindowWidthSizeType[] = [
        new WindowWidthSizeType(
            WindowTypeName.DESKTOP,
            Range.of(760, Number.MAX_VALUE),
        ),
        // new WindowWidthSizeType(WindowTypeName.TABLET, Range.of(1000, 1479)),
        new WindowWidthSizeType(WindowTypeName.MOBILE, Range.of(0, 759)),
    ];

    /**
     * Идентифицирует тип окна по его ширине.
     *
     * Если не попадает ни в один из диапазонов {@link WindowWidthSizeType.windowWidthSizeTypes}, то вернет {@link WindowTypeName.DESKTOP}
     * @param width - ширина.
     */
    static getTypeNameByWidth(width: number): WindowTypeName {
        const type = WindowWidthSizeType.windowWidthSizeTypes.find(
            ({ range }) => range.isInBound(width),
        );
        if (type) {
            return type.name;
        } else {
            return WindowTypeName.DESKTOP;
        }
    }
}

/**
 * Хук предоставляющий данные о текущем размере экрана.
 *
 * WindowTypeName: {@link WindowTypeName} - название текущего размера окна.
 * @return [WindowTypeName]
 */
export function useWindowType(): [WindowTypeName] {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useSubscribeOn(window, 'resize', () => setWindowWidth(window.innerWidth));

    const result = WindowWidthSizeType.getTypeNameByWidth(windowWidth);

    return [result];
}

export function useIsDesktop(): [boolean] {
    const [windowType] = useWindowType();
    return [windowType === WindowTypeName.DESKTOP];
}

export function windowTypeToClassName(windowType: WindowTypeName): string {
    const mappingWindowType = MapBuilder.create<WindowTypeName, string>()
        .value(WindowTypeName.DESKTOP, 'desktop')
        .value(WindowTypeName.TABLET, 'tablet')
        .value(WindowTypeName.MOBILE, 'mobile')
        .build();

    return mappingWindowType.get(windowType);
}
